import { useState, useMemo, useEffect } from 'react';
import { api } from './api';

export const DEFAULT_TZ = 'America/Argentina/Buenos_Aires';

// Zonas horarias disponibles para seleccionar en el panel
export const TZ_OPTIONS = [
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina (UTC-3)' },
  { value: 'America/Sao_Paulo',              label: 'Brasil / São Paulo (UTC-3)' },
  { value: 'America/Santiago',               label: 'Chile (UTC-3/-4)' },
  { value: 'America/Bogota',                 label: 'Colombia (UTC-5)' },
  { value: 'America/Lima',                   label: 'Perú (UTC-5)' },
  { value: 'America/Mexico_City',            label: 'México (UTC-6)' },
  { value: 'America/New_York',               label: 'EE.UU. Este (UTC-5/-4)' },
  { value: 'America/Chicago',                label: 'EE.UU. Central (UTC-6/-5)' },
  { value: 'America/Denver',                 label: 'EE.UU. Montaña (UTC-7/-6)' },
  { value: 'America/Los_Angeles',            label: 'EE.UU. Pacífico (UTC-8/-7)' },
  { value: 'Europe/Madrid',                  label: 'España (UTC+1/+2)' },
  { value: 'Europe/London',                  label: 'Reino Unido (UTC+0/+1)' },
  { value: 'Europe/Paris',                   label: 'Europa Central (UTC+1/+2)' },
  { value: 'UTC',                            label: 'UTC' },
];

/**
 * Normaliza un string de fecha proveniente del backend a un Date UTC correcto.
 *
 * FastAPI serializa DateTime(timezone=True) sin sufijo de zona cuando
 * el valor está en UTC (p. ej. "2026-03-19T16:00:00"), lo que hace que
 * `new Date()` lo interprete como hora local del browser en lugar de UTC.
 * Esta función agrega la 'Z' si el string no trae ningún offset explícito.
 */
export function parseUTC(d) {
  if (!d) return null;
  if (d instanceof Date) return d;
  const s = String(d);
  // Si ya tiene info de zona (Z, +HH:MM, -HH:MM) lo dejamos tal cual
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  // Sin zona → asumimos UTC (el backend siempre guarda en UTC)
  return new Date(s + 'Z');
}

/**
 * Formatea una fecha ISO en la zona horaria indicada.
 * Devuelve: "17/03/2026 15:42 ART" — fecha, hora y abrev. de zona.
 *
 * @param {string|Date} d  — fecha ISO o Date object
 * @param {string} tz      — IANA timezone string (default: ART)
 * @returns {string}
 */
export function formatDateTime(d, tz = DEFAULT_TZ) {
  if (!d) return '—';
  try {
    const date = parseUTC(d);
    const datePart = date.toLocaleDateString('es-AR', {
      timeZone: tz,
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
    const timePart = date.toLocaleTimeString('es-AR', {
      timeZone: tz,
      hour: '2-digit', minute: '2-digit',
    });
    const tzAbbrPart = date.toLocaleTimeString('es-AR', {
      timeZone: tz,
      timeZoneName: 'short',
    }).split(' ').pop();           // "GMT-3" → "GMT-3"
    return `${datePart} ${timePart} ${tzAbbrPart}`;
  } catch {
    // Si el navegador no soporta la tz (raro), fallback sin zona
    return new Date(d).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
  }
}

/**
 * Convierte una expresión cron simple a texto legible en español.
 * Cubre los patrones más comunes usados en Taxco BaaS.
 * Para expresiones no reconocidas devuelve la expresión original.
 *
 * @param {string} cron  — expresión cron de 5 campos (min hora dom mes dow)
 * @returns {string}
 */
export function formatCron(cron) {
  if (!cron) return '—';
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;

  const [min, hour, dom, month, dow] = parts;

  const pad   = n => String(n).padStart(2, '0');
  const hhmm  = (h, m) => `${pad(h)}:${pad(m)}`;

  const DAYS  = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
  const MONTHS = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

  const isNum = s => /^\d+$/.test(s);
  const every = s => s === '*';

  // @daily / cada día a una hora fija
  if (isNum(min) && isNum(hour) && every(dom) && every(month) && every(dow))
    return `Todos los días a las ${hhmm(hour, min)}`;

  // Día de semana específico
  if (isNum(min) && isNum(hour) && every(dom) && every(month) && isNum(dow))
    return `Todos los ${DAYS[+dow] || 'dow='+dow} a las ${hhmm(hour, min)}`;

  // Varios días de semana (1-5, 1,3,5, etc.)
  if (isNum(min) && isNum(hour) && every(dom) && every(month) && dow.includes(',')) {
    const names = dow.split(',').map(d => DAYS[+d] || d).join(', ');
    return `${names} a las ${hhmm(hour, min)}`;
  }
  if (isNum(min) && isNum(hour) && every(dom) && every(month) && dow.includes('-')) {
    const [from, to] = dow.split('-');
    return `${DAYS[+from] || from} a ${DAYS[+to] || to} a las ${hhmm(hour, min)}`;
  }

  // Día específico del mes
  if (isNum(min) && isNum(hour) && isNum(dom) && every(month) && every(dow))
    return `Día ${dom} de cada mes a las ${hhmm(hour, min)}`;

  // Cada N horas
  if (isNum(min) && hour.startsWith('*/') && every(dom) && every(month) && every(dow)) {
    const n = hour.slice(2);
    return `Cada ${n} horas (minuto :${pad(min)})`;
  }

  // Cada N minutos
  if (min.startsWith('*/') && every(hour) && every(dom) && every(month) && every(dow)) {
    return `Cada ${min.slice(2)} minutos`;
  }

  // Lunes a viernes (1-5)
  if (isNum(min) && isNum(hour) && every(dom) && every(month) && dow === '1-5')
    return `Lunes a viernes a las ${hhmm(hour, min)}`;

  // Fines de semana (0,6 o 6,0)
  if (isNum(min) && isNum(hour) && every(dom) && every(month) && (dow === '0,6' || dow === '6,0'))
    return `Sábado y domingo a las ${hhmm(hour, min)}`;

  // Fallback: mostrar la expresión pero más legible
  return cron;
}

export function formatBytes(b) {
  if (!b) return '—';
  if (b > 1e9) return (b / 1e9).toFixed(1) + ' GB';
  if (b > 1e6) return (b / 1e6).toFixed(1) + ' MB';
  if (b > 1e3) return (b / 1e3).toFixed(1) + ' KB';
  return b + ' B';
}

export function timeAgo(d) {
  if (!d) return '—';
  const s = Math.floor((Date.now() - parseUTC(d)) / 1000);
  if (s < 60) return 'hace ' + s + 's';
  if (s < 3600) return 'hace ' + Math.floor(s / 60) + 'min';
  if (s < 86400) return 'hace ' + Math.floor(s / 3600) + 'h';
  return 'hace ' + Math.floor(s / 86400) + 'd';
}

export function formatDate(d) {
  if (!d) return '—';
  return parseUTC(d).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}

export function useSortable(data, defaultKey, defaultDir = 'desc') {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const toggle = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = useMemo(() => {
    if (!data || !sortKey) return data;
    return [...data].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggle };
}

export function SortTh({ label, colKey, sortKey, sortDir, onToggle, style }) {
  const active = colKey === sortKey;
  return (
    <th onClick={() => onToggle(colKey)}
        style={{ cursor: 'pointer', userSelect: 'none',
                 color: active ? 'var(--accent)' : 'var(--muted)', ...style }}>
      {label}
      <span style={{ marginLeft: 4, fontSize: 10, opacity: active ? 1 : 0.3 }}>
        {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </th>
  );
}

// #158 D2 — texto del badge secundario "heartbeat ✓ / WS ✗".
export const WS_WARNING_TITLE =
  'Agente vivo (heartbeat OK) pero sin canal de comandos: su WebSocket está caído. ' +
  'No puede recibir órdenes (backups manuales, exploración, restore) hasta que reconecte.';

export function heartbeatStatus(last_seen, ws_connected) {
  if (!last_seen) return { color: 'var(--muted)', label: 'sin contacto', wsWarning: false };
  const ageMin = (Date.now() - new Date(last_seen).getTime()) / 60000;
  // #158 D2 — señal SECUNDARIA: el color primario sigue siendo el heartbeat.
  // Sólo marca el caso "el agente late por HTTP pero su WS está caído"; con
  // ws_connected === undefined (el caller no tiene el dato) no marca nada.
  const wsWarning = ws_connected === false && ageMin < 15;
  if (ageMin < 5)  return { color: 'var(--green)',  label: 'online',  wsWarning };
  if (ageMin < 15) return { color: 'var(--yellow)', label: 'idle',    wsWarning };
  return               { color: 'var(--red)',    label: 'offline', wsWarning: false };
}

// #158 D1 — El indicador de agente calculaba su estado contra Date.now() sobre
// un last_seen que nunca se refrescaba: la consola abierta viraba sola a
// amarillo a los 5 min y a rojo a los 15, sin que pasara nada. Este hook
// repolea el endpoint liviano GET /api/servers/heartbeats cada 30 s (mismo
// ritmo que AuditLog y el widget de usuarios del Dashboard) y devuelve el
// dato fresco para que el cálculo de heartbeatStatus vuelva a tener sentido.
export function useServerHeartbeats(clientId) {
  const [heartbeats, setHeartbeats] = useState({});
  const [ok, setOk] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let failures = 0;
    let timer = null;

    const tick = async () => {
      try {
        const data = await api.getServerHeartbeats(clientId);
        if (cancelled) return;
        failures = 0;
        setHeartbeats(data || {});
        setOk(true);
      } catch {
        if (cancelled) return;
        // Degradación consciente: si el feed falla de forma persistente
        // (backend caído, token vencido) marcamos ok=false para que las
        // vistas muestren "sin datos" en vez de degradar en silencio al
        // last_seen congelado — que es exactamente el bug reportado.
        failures += 1;
        if (failures >= 3) setOk(false);
      }
    };

    const start = () => {
      if (timer) return;
      tick();
      timer = setInterval(tick, 30000);
    };
    const stop = () => { clearInterval(timer); timer = null; };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();   // start() hace tick() inmediato: al volver a la pestaña
                      // pide el dato YA, no espera hasta 30 s al próximo ciclo.
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [clientId]);

  return { heartbeats, ok };
}

// #158 D2 — marca secundaria "sin canal de comandos" al lado del indicador
// de heartbeat. No reemplaza el color del punto; lo acompaña.
export function WsWarnBadge({ style }) {
  return (
    <span
      title={WS_WARNING_TITLE}
      style={{
        marginLeft: 4, fontSize: 9, lineHeight: 1, fontFamily: 'var(--mono)',
        color: 'var(--yellow)', border: '1px solid var(--yellow)',
        borderRadius: 2, padding: '0 2px', letterSpacing: 0.5, flexShrink: 0,
        ...style,
      }}
    >
      WS✕
    </span>
  );
}

// Resuelve el estado de UN servidor combinando su dato inicial (server.last_seen,
// el que vino con la lista) con el feed en vivo de useServerHeartbeats.
export function liveHeartbeat(server, feed) {
  const { heartbeats, ok } = feed || { heartbeats: {}, ok: true };
  if (!ok) return { color: 'var(--muted)', label: 'sin datos', stale: true };
  const entry = heartbeats[server.id];
  return heartbeatStatus(entry?.last_seen ?? server.last_seen, entry?.ws_connected);
}

export function versionCompare(a, b) {
  const pa = (a || '').split('.').map(Number);
  const pb = (b || '').split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    if ((pa[i] || 0) > (pb[i] || 0)) return 1;
    if ((pa[i] || 0) < (pb[i] || 0)) return -1;
  }
  return 0;
}

export function isUpdateAvailable(agentVersion, latestVersion) {
  if (!latestVersion || !agentVersion) return false;
  return versionCompare(latestVersion, agentVersion) > 0;
}

export function isAheadOfChannel(agentVersion, latestVersion) {
  if (!latestVersion || !agentVersion) return false;
  return versionCompare(agentVersion, latestVersion) > 0;
}

function parseVersionParts(v) {
  return (v || '').toString().split('.').map(Number);
}

export function meetsVersionRequirement(actual, minimum) {
  const pa = parseVersionParts(actual);
  const pb = parseVersionParts(minimum);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const a = pa[i] || 0, b = pb[i] || 0;
    if (a > b) return true;
    if (a < b) return false;
  }
  return true;
}

// Conservador igual que unmet_requirements() del backend (app/services/
// agent_versioning.py): sin entorno o sin requisitos declarados, no marca
// nada como incumplido; un campo puntual ausente en el entorno tampoco.
export function unmetRequirements(environment, requirements) {
  if (!requirements || Object.keys(requirements).length === 0) return [];
  if (!environment) return [];
  const unmet = [];
  for (const [key, minVersion] of Object.entries(requirements)) {
    const actual = environment[key];
    if (!actual) continue;
    if (!meetsVersionRequirement(actual, minVersion)) {
      unmet.push({ key, actual, minVersion });
    }
  }
  return unmet;
}

const FILTER_TREE_MAX_MATCHES = 200;

function normalizeForSearch(s) {
  return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

// Filtra el árbol de un snapshot (ya en memoria) por nombre de archivo/carpeta.
// Devuelve coincidencias en una lista plana con la ruta completa de cada una.
// Si hay más de FILTER_TREE_MAX_MATCHES, corta la búsqueda y marca truncated
// para que la UI muestre un aviso en vez de renderizar todo.
export function filterTree(tree, query) {
  const q = normalizeForSearch(query).trim();
  if (!q) return { matches: [], truncated: false };

  const matches = [];
  let truncated = false;

  function walk(node, name) {
    if (truncated || !node || typeof node !== 'object') return;
    const meta = node.__meta;
    if (meta?.path && normalizeForSearch(name).includes(q)) {
      if (matches.length >= FILTER_TREE_MAX_MATCHES) { truncated = true; return; }
      matches.push({
        path: meta.path.replace(/\\/g, '/'),
        name,
        isDir: meta.type === 'dir',
        size: meta.size,
        node,
      });
    }
    for (const [k, v] of Object.entries(node)) {
      if (k === '__meta') continue;
      walk(v, k);
      if (truncated) return;
    }
  }

  for (const [k, v] of Object.entries(tree || {})) {
    if (k === '__meta') continue;
    walk(v, k);
    if (truncated) break;
  }

  return { matches, truncated };
}

// #145: restic reconstruye la ruta absoluta original del archivo debajo del
// target del restore. Esta función es sólo para mostrarle al usuario dónde
// va a aparecer cada archivo — no participa del restore en sí, que sigue
// siendo un --target plano pasado tal cual al agente.
//
// El caso POSIX (target + ruta absoluta) es el confirmado en producción. El
// caso Windows (la letra de unidad "C:" pasa a ser una carpeta "C" debajo
// del target) es el comportamiento documentado de restic, pero no se pudo
// verificar contra una restauración real en Windows en este entorno.
export function computeRestorePreviewPath(sourcePath, targetPath) {
  if (!sourcePath || !targetPath) return null;

  const isWindowsSource = /^[a-zA-Z]:[\\/]/.test(sourcePath);
  const targetTrimmed = targetPath.replace(/[\\/]+$/, '');
  if (!targetTrimmed) return null;
  const usesBackslash = targetTrimmed.includes('\\') && !targetTrimmed.includes('/');
  const sep = usesBackslash ? '\\' : '/';

  let tail;
  if (isWindowsSource) {
    const drive = sourcePath[0].toUpperCase();
    const rest = sourcePath.slice(2).replace(/^[\\/]+/, '').replace(/[\\/]/g, sep);
    tail = drive + sep + rest;
  } else {
    tail = sourcePath.replace(/^\/+/, '').replace(/[\\/]/g, sep);
  }

  return `${targetTrimmed}${sep}${tail}`;
}

export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}
